import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounceCallback } from '@react-hook/debounce';
import { createSensorAuthorization as createSensorAuthorizationCaller } from 'state/callers';
import {
  DD_LOGS_CONSTS,
  GIT_PROVIDERS,
  LOCAL_STORAGE_KEYS,
  SESSION_STORAGE_KEYS,
  TRACKING
} from 'common/constants/constants';
import { parseJwt, redirectToProvider } from 'common/utils/utils';
import * as TrackingService from 'common/services/tracking.service';
import axios from 'common/requester';
import { AUTH_METHODS, GIT_PROVIDERS_CONFIG } from '../consts';
import {
  getProviderConfig,
  validationStatusToMessageMap
} from 'common/utils/provider.utils';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import { requestInvitation } from 'modals/LoginModal/components/YouLookFamiliar/you-look-familiar.service';
import { myAccountSelector } from 'state/selectors/account';

const useGitProviderSetupData = ({
  selectedProvider,
  selectedMethod,
  setSelectedMethod
}) => {
  const dispatch = useDispatch();
  const { email } = useSelector(myAccountSelector);

  const [pat, setPat] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiTestSuccessfully, setApiTestSuccessfully] = useState(false);
  const [failurePayload, setFailurePayload] = useState(null);
  const [orgName, setOrgName] = useState('');
  const [username, setUsername] = useState('');
  const [isInviteAdminModalOpen, setIsInviteAdminModalOpen] = useState(false);
  const [confirmFamiliarOrgCreation, setConfirmFamiliarOrgCreation] =
    useState(false);
  const sessionStorageAccessor = BrowserStorageAccessor().session;
  const requestInvitationToken = sessionStorageAccessor.get(
    SESSION_STORAGE_KEYS.REQUEST_INVITATION_TOKEN
  );
  const familiarOrgName = requestInvitationToken
    ? sessionStorageAccessor.get(SESSION_STORAGE_KEYS.ORGANIZATION_NAME)
    : null;

  const localStorageAccessor = BrowserStorageAccessor().local;
  useEffect(() => {
    if (requestInvitationToken) {
      const { context } = parseJwt(requestInvitationToken);
      const { requesterProviderId, requestedOrgId } = context;
      window.addEventListener(
        'load',
        () => {
          TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
            page: TRACKING.PAGES.ONBOARDING,
            page_tab: TRACKING.PAGE_TABS.YOU_LOOK_FAMILIAR,
            context: {
              providerId: requesterProviderId,
              orgId: requestedOrgId
            }
          });

          window?.DD_LOGS.logger.info(
            DD_LOGS_CONSTS.strayOnBoardings.startedFlow,
            {
              requestedOrgId,
              requesterProviderId
            }
          );
        },
        false
      );
    }
  }, []);
  const onAskForInvite = async () => {
    const { context } = parseJwt(requestInvitationToken);
    const { requesterProviderId, requestedOrgId, requesterNameAndEmail } =
      context;

    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.ONBOARDING,
      page_tab: TRACKING.PAGE_TABS.YOU_LOOK_FAMILIAR,
      click_type: TRACKING.CLICK.STRAY_REQUEST_INVITATION,
      context: {
        requestedOrgId,
        requesterProviderId
      }
    });
    window?.DD_LOGS?.logger.info(
      DD_LOGS_CONSTS.strayOnBoardings.requestInvitation,
      {
        requestedOrgId,
        requester: requesterNameAndEmail,
        requesterProviderId
      }
    );

    await requestInvitation(requestInvitationToken);
  };
  const createSensorAuthorization = body =>
    dispatch(createSensorAuthorizationCaller(body));

  const providerId = GIT_PROVIDERS[selectedProvider.toUpperCase()];
  const {
    showOrgNameField,
    baseUrlForTest: configBaseUrlForTest,
    baseUrl,
    name: providerName,
    apiTestUrl
  } = getProviderConfig({ provider: selectedProvider, isCloudProvider: false });
  const tokenDisabled = showOrgNameField ? loading || !orgName : loading;
  const testApiConnectivityEnabled = showOrgNameField ? pat && orgName : pat;
  const baseUrlForTest = configBaseUrlForTest
    ? { base_url: configBaseUrlForTest(baseUrl, orgName) }
    : {};
  const isPat = selectedMethod === AUTH_METHODS.PAT;

  const onConnect = async () => {
    TrackingService.logEvent(TRACKING.EVENTS.SETUP_WIZARD_CONNECT_GIT_START, {
      identity_email: email,
      git_provider: selectedProvider,
      auth: isPat ? AUTH_METHODS.PAT : AUTH_METHODS.OAUTH,
      is_familiar: !!familiarOrgName
    });

    localStorageAccessor.set(
      LOCAL_STORAGE_KEYS.SHOULD_LOG_GIT_CONNECT_SUCCESS,
      true
    );
    if (isPat) {
      const body = {
        sensor_id: providerId,
        state: 'ok',
        token: {
          token: pat,
          refresh_token: null,
          is_proxied: false
        },
        profile: {
          username,
          ...baseUrlForTest
        },
        name: showOrgNameField ? `${providerName} - ${orgName}` : providerName
      };
      await createSensorAuthorization(body);
    } else {
      redirectToProvider(selectedProvider);
    }
  };

  const testApiConnectivity = useDebounceCallback(async () => {
    const body = {
      access_token: pat,
      ...baseUrlForTest
    };

    setFailurePayload(null);
    setLoading(true);

    const { token_status, missing_scopes, username } = await axios.post(
      apiTestUrl,
      body
    );

    if (token_status === validationStatusToMessageMap().VALID) {
      setTimeout(() => {
        setLoading(false);
        setUsername(username);
        setApiTestSuccessfully(true);
      }, 2000);

      return;
    }

    setTimeout(() => {
      setFailurePayload({
        tokenStatus: token_status,
        missingScopes: missing_scopes
      });

      setApiTestSuccessfully(false);
      setLoading(false);
    }, 2000);
  }, 1000);

  useEffect(() => {
    setSelectedMethod(
      GIT_PROVIDERS_CONFIG[selectedProvider][AUTH_METHODS.OAUTH]
        ? AUTH_METHODS.OAUTH
        : AUTH_METHODS.PAT
    );
    setPat('');
  }, [selectedProvider]);

  useEffect(() => {
    setFailurePayload(null);
  }, [selectedProvider, selectedMethod]);

  useEffect(() => {
    setPat(pat?.trim());
    setApiTestSuccessfully(false);
    if (testApiConnectivityEnabled) {
      testApiConnectivity();
    } else {
      setApiTestSuccessfully(false);
    }
  }, [pat, orgName]);

  useEffect(() => {
    if (!!failurePayload) {
      TrackingService.logEvent(TRACKING.EVENTS.SETUP_WIZARD_CONNECT_GIT_FAIL, {
        identity_email: email,
        git_provider: selectedProvider,
        auth: isPat ? AUTH_METHODS.PAT : AUTH_METHODS.OAUTH,
        reason: failurePayload.tokenStatus
      });
    }
  }, [failurePayload]);
  return {
    pat,
    setPat,
    onConnect,
    apiTestSuccessfully,
    tokenDisabled,
    failurePayload,
    showOrgNameField,
    orgName,
    setOrgName,
    isInviteAdminModalOpen,
    setIsInviteAdminModalOpen,
    onAskForInvite,
    familiarOrgName,
    email,
    setFailurePayload,
    confirmFamiliarOrgCreation,
    setConfirmFamiliarOrgCreation
  };
};

export default useGitProviderSetupData;
